/**
 * FL-132: デモ取引先データ
 *
 * 案件データで参照される取引先の基本情報
 * 固定IDを使用して案件との関連性を保証
 */

import {
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  BusinessPartnerType,
  BusinessPartnerStatus,
  BusinessPartnerRole,
} from '../../types';

/**
 * デモ取引先データ
 * caseData.tsで参照されるIDと一致させる
 */
export const DEMO_PARTNERS: Omit<BusinessPartner, 'companyId' | 'createdAt' | 'updatedAt'>[] = [
  // 自動車販売店A（請求先・納車先）
  {
    id: '11111111-1111-1111-1111-111111111111',
    name: '株式会社オートディーラーA',
    type: BusinessPartnerType.CORPORATE,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.DEALER,
    availableRoles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
    locations: [
      {
        id: '11111111-1111-1111-1111-111111111111-default',
        name: '本社',
        address: '東京都港区六本木1-1-1 オートディーラーAビル',
        phone: '048-825-0001',
        contactPerson: '営業部',
        email: 'info@auto-dealer-a.co.jp',
        isDefault: true,
        roles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
      },
    ],
    defaultAddress: '東京都港区六本木1-1-1 オートディーラーAビル',
    defaultPhone: '048-825-0001',
    defaultContactPerson: '営業部',
    defaultEmail: 'info@auto-dealer-a.co.jp',
    isSystemPreset: false,
    notes: 'デモ用自動車販売店A - 請求・納車対応',
  },

  // 自動車販売店B（請求先・納車先）
  {
    id: '22222222-2222-2222-2222-222222222222',
    name: '株式会社オートディーラーB',
    type: BusinessPartnerType.CORPORATE,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.DEALER,
    availableRoles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
    locations: [
      {
        id: '22222222-2222-2222-2222-222222222222-default',
        name: '本社',
        address: '神奈川県横浜市西区みなとみらい2-2-2 オートディーラーBタワー',
        phone: '045-662-0001',
        contactPerson: '営業部',
        email: 'contact@auto-dealer-b.co.jp',
        isDefault: true,
        roles: [BusinessPartnerRole.BILLING, BusinessPartnerRole.DELIVERY],
      },
    ],
    defaultAddress: '神奈川県横浜市西区みなとみらい2-2-2 オートディーラーBタワー',
    defaultPhone: '045-662-0001',
    defaultContactPerson: '営業部',
    defaultEmail: 'contact@auto-dealer-b.co.jp',
    isSystemPreset: false,
    notes: 'デモ用自動車販売店B - 請求・納車対応',
  },

  // 自動車販売店C（引取先）
  {
    id: '33333333-3333-3333-3333-333333333333',
    name: '株式会社オートディーラーC',
    type: BusinessPartnerType.CORPORATE,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.DEALER,
    availableRoles: [BusinessPartnerRole.PICKUP],
    locations: [
      {
        id: '33333333-3333-3333-3333-333333333333-default',
        name: '本社',
        address: '東京都渋谷区神宮前3-3-3 オートディーラーC本社',
        phone: '03-5432-0001',
        contactPerson: '営業部',
        email: 'pickup@auto-dealer-c.co.jp',
        isDefault: true,
        roles: [BusinessPartnerRole.PICKUP],
      },
    ],
    defaultAddress: '東京都渋谷区神宮前3-3-3 オートディーラーC本社',
    defaultPhone: '03-5432-0001',
    defaultContactPerson: '営業部',
    defaultEmail: 'pickup@auto-dealer-c.co.jp',
    isSystemPreset: false,
    notes: 'デモ用自動車販売店C - 引取専門',
  },

  // オークション会場（引取先）
  {
    id: '44444444-4444-4444-4444-444444444444',
    name: '東京オートオークション',
    type: BusinessPartnerType.CORPORATE,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.AUCTION,
    availableRoles: [BusinessPartnerRole.PICKUP],
    locations: [
      {
        id: '44444444-4444-4444-4444-444444444444-default',
        name: '本社',
        address: '東京都江東区有明4-4-4 東京オートオークション会場',
        phone: '03-3799-0001',
        contactPerson: '受付',
        email: 'info@tokyo-auto-auction.co.jp',
        isDefault: true,
        roles: [BusinessPartnerRole.PICKUP],
      },
    ],
    defaultAddress: '東京都江東区有明4-4-4 東京オートオークション会場',
    defaultPhone: '03-3799-0001',
    defaultContactPerson: '受付',
    defaultEmail: 'info@tokyo-auto-auction.co.jp',
    isSystemPreset: false,
    notes: 'デモ用オークション会場 - 車両引取',
  },

  // 個人顧客A（引取先・納車先）
  {
    id: '55555555-5555-5555-5555-555555555555',
    name: '山田 太郎',
    type: BusinessPartnerType.INDIVIDUAL,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.OTHER,
    availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
    locations: [
      {
        id: '55555555-5555-5555-5555-555555555555-default',
        name: '自宅',
        address: '東京都世田谷区成城5-5-5 成城マンション101',
        phone: '080-1234-5678',
        contactPerson: '山田 太郎',
        email: 'yamada.taro@example.com',
        isDefault: true,
        roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
      },
    ],
    defaultAddress: '東京都世田谷区成城5-5-5 成城マンション101',
    defaultPhone: '080-1234-5678',
    defaultContactPerson: '山田 太郎',
    defaultEmail: 'yamada.taro@example.com',
    isSystemPreset: false,
    notes: 'デモ用個人顧客A - 引取・納車対応',
  },

  // 個人顧客B（引取先・納車先）
  {
    id: '66666666-6666-6666-6666-666666666666',
    name: '佐藤 花子',
    type: BusinessPartnerType.INDIVIDUAL,
    status: BusinessPartnerStatus.ACTIVE,
    dataSource: BusinessPartnerDataSource.CUSTOM,
    category: BusinessPartnerCategory.OTHER,
    availableRoles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
    locations: [
      {
        id: '66666666-6666-6666-6666-666666666666-default',
        name: '自宅',
        address: '東京都世田谷区奥沢6-6-6',
        phone: '080-9876-5432',
        contactPerson: '佐藤 花子',
        email: 'sato.hanako@example.com',
        isDefault: true,
        roles: [BusinessPartnerRole.PICKUP, BusinessPartnerRole.DELIVERY],
      },
    ],
    defaultAddress: '東京都世田谷区奥沢6-6-6',
    defaultPhone: '080-9876-5432',
    defaultContactPerson: '佐藤 花子',
    defaultEmail: 'sato.hanako@example.com',
    isSystemPreset: false,
    notes: 'デモ用個人顧客B - 引取・納車対応',
  },
];

/**
 * デモ取引先データをcompany_id付きで準備
 */
export function prepareDemoPartners(
  companyId: string
): Omit<BusinessPartner, 'createdAt' | 'updatedAt'>[] {
  return DEMO_PARTNERS.map(partner => ({
    ...partner,
    companyId,
  }));
}
